import React from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Building, Plus, Globe, Edit } from "lucide-react";

const prep = (v) => ({
  ...v,
  documents: typeof v.documents === "string" ? v.documents.split(",").map((s) => s.trim()).filter(Boolean) : v.documents,
  public_enabled: v.public_enabled === "yes" || v.public_enabled === true,
});

export default function Lenders() {
  const { hasPerm } = useAuth();
  const { items, loading, refetch } = useList("/loan/lenders", {});
  const create = async (v) => { await api.post("/loan/lenders", prep(v)); refetch(); };
  const edit = async (l, v) => { await api.put(`/loan/lenders/${l.id}`, prep(v)); refetch(); };
  const fields = [
    { name: "name", label: "Lender Name", required: true },
    { name: "institution_type", label: "Institution Type", type: "select", options: ["Bank", "NBFC", "Fintech", "DSA/Partner", "Other"] },
    { name: "website", label: "Website" },
    { name: "partner_portal", label: "Partner Portal URL" },
    { name: "public_apply_url", label: "Public 'Apply' link (https only, approved by you)" },
    { name: "public_enabled", label: "Show on public loan page?", type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
    { name: "rm_name", label: "RM Name" },
    { name: "contact", label: "Contact" },
    { name: "documents", label: "Required Documents (comma-separated — auto-fills checklist)", type: "textarea", full: true },
    { name: "commission_terms", label: "Commission / Referral Terms" },
    { name: "settlement_terms", label: "Payment & Settlement Terms" },
    { name: "internal_notes", label: "Internal Notes", type: "textarea", full: true },
  ];
  const columns = [
    { key: "name", header: "Lender", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">{r.institution_type}</div></div> },
    { key: "rm_name", header: "RM", render: (r) => <span className="text-sm">{r.rm_name || "—"}</span> },
    { key: "documents", header: "Required Docs", render: (r) => <span className="text-sm text-muted-foreground">{(r.documents || []).join(", ") || "—"}</span> },
    { key: "commission_terms", header: "Commission Terms", render: (r) => <span className="text-sm text-muted-foreground">{r.commission_terms || "—"}</span> },
    { key: "website", header: "Website", render: (r) => r.website ? <a href={r.website} target="_blank" rel="noreferrer" className="text-primary text-sm inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" />Visit</a> : "—" },
    { key: "active", header: "Status", render: (r) => <div className="space-y-1"><StatusBadge value={r.active ? "active" : "lost"} />{r.public_enabled && <div className="text-[11px] text-emerald-600 font-semibold" data-testid="lender-public-flag">On public page</div>}</div> },
    ...(hasPerm("lenders:edit") ? [{ key: "actions", header: "", render: (r) => (
      <div onClick={(e) => e.stopPropagation()}>
        <FormDialog title={`Edit ${r.name}`} fields={fields} initial={{ ...r, documents: (r.documents || []).join(", "), public_enabled: r.public_enabled ? "yes" : "no" }} onSubmit={(v) => edit(r, v)} submitLabel="Update"
          trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid="lender-edit-btn"><Edit className="h-4 w-4" /></Button>} />
      </div>
    ) }] : []),
  ];
  return (
    <div>
      <PageHeader title="Lenders / Banks / NBFCs" subtitle="Editable lender directory · commercial terms role-restricted" icon={Building}
        actions={hasPerm("lenders:add") && (
          <FormDialog title="Add Lender" fields={fields} onSubmit={create} submitLabel="Add Lender" testId="create-lender-dialog"
            trigger={<Button data-testid="create-lender-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Lender</Button>} />
        )} />
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="lender-table-row" />
    </div>
  );
}
